/**
 * Funding — live funding orders, manual credits and unmatched receipts.
 */
import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { PageHead } from "../components/PageHead";
import { EmptyState, ErrorCard, LoadingCard } from "../components/Async";
import {
  useAdminQuery,
  downloadCsv,
  shortDateTime,
  tzs,
  errorMessage,
  type Paged,
} from "../lib/admin";
import { adminApi } from "../lib/api";
import type { ClearingRow, FundingRow } from "../lib/types";
import {
  Download,
  ArrowDown,
  ArrowUp,
  Check,
  X,
  ChevronLeft,
  ChevronRight,
  AlertTriangle,
  Banknote,
} from "lucide-react";

const statusColor: Record<string, string> = {
  credited: "var(--ev-mint-600)",
  pending: "var(--ev-amber-600)",
  awaiting_payment: "var(--ev-amber-600)",
  failed: "var(--ev-red-600)",
  expired: "var(--ev-slate-500)",
};

const statusBg: Record<string, string> = {
  credited: "var(--ev-mint-100)",
  pending: "var(--ev-amber-100)",
  awaiting_payment: "var(--ev-amber-100)",
  failed: "var(--ev-red-100)",
  expired: "var(--ev-slate-100)",
};

export function Funding() {
  const queryClient = useQueryClient();
  const [statusFilter, setStatusFilter] = useState("all");
  const [page, setPage] = useState(1);
  const [agentId, setAgentId] = useState("");
  const [amount, setAmount] = useState("");
  const [reference, setReference] = useState("");
  const [feedback, setFeedback] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [walletInputs, setWalletInputs] = useState<Record<string, string>>({});
  const pageSize = 25;

  const list = useAdminQuery<Paged<FundingRow>>(
    ["funding", statusFilter, page],
    "/admin/v1/funding",
    { status: statusFilter === "all" ? undefined : statusFilter, page, page_size: pageSize },
  );
  const clearing = useAdminQuery<{ unmatched: ClearingRow[] }>(["funding-clearing"], "/admin/v1/funding/clearing");

  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: ["admin"] });
    setActionError(null);
  };

  const credit = useMutation({
    mutationFn: (input: { agent_id: string; amount_minor: number; reference: string }) =>
      adminApi.post<{ payment_reference: string }>("/admin/v1/funding/manual-credit", undefined, {
        query: input,
      }),
    onSuccess: (result) => {
      setFeedback(`Credited ${result.payment_reference}`);
      setAmount("");
      setReference("");
      invalidate();
    },
    onError: (error) => setActionError(errorMessage(error)),
  });

  const allocate = useMutation({
    mutationFn: (input: { ref: string; wallet_id: string }) =>
      adminApi.post("/admin/v1/funding/" + input.ref + "/allocate", undefined, {
        query: { wallet_id: input.wallet_id },
      }),
    onSuccess: () => { setFeedback("Receipt allocated"); invalidate(); },
    onError: (error) => setActionError(errorMessage(error)),
  });

  const rows = list.data?.data ?? [];
  const total = list.data?.meta?.total ?? rows.length;
  const credited = rows.filter((r) => r.status === "credited").reduce((s, r) => s + r.amount_minor, 0);
  const pending = rows.filter((r) => r.status === "pending" || r.status === "awaiting_payment").length;
  const unmatched = clearing.data?.unmatched ?? [];

  const exportCsv = () => {
    downloadCsv(
      "evocha-funding.csv",
      ["Reference", "Agent", "Amount", "Method", "Status", "Provider ref", "Credited", "Created"],
      rows.map((f) => [
        f.payment_reference,
        f.agent?.business_name ?? f.agent_id,
        f.amount_minor / 100,
        f.method,
        f.status,
        f.provider_ref ?? "",
        f.credited_at ?? "",
        f.created_at ?? "",
      ]),
    );
  };

  return (
    <>
      <PageHead
        title="Funding"
        description="Funding orders, manual credits, and unmatched receipts."
        actions={
          <button className="ev-btn ev-btn-sm ev-btn-outline" onClick={exportCsv} disabled={rows.length === 0}>
            <Download size={16} /> Export
          </button>
        }
      />

      <div className="admin-kpi-grid" style={{ marginBottom: 24 }}>
        <div className="admin-card">
          <span className="ev-body-muted">Credited (this page)</span>
          <div className="ev-display-s" style={{ color: "var(--ev-mint-600)" }}>{tzs(credited)}</div>
        </div>
        <div className="admin-card">
          <span className="ev-body-muted">Pending (this page)</span>
          <div className="ev-display-s" style={{ color: "var(--ev-amber-600)" }}>{pending}</div>
        </div>
        <div className="admin-card">
          <span className="ev-body-muted">Unmatched receipts</span>
          <div className="ev-display-s" style={{ color: "var(--ev-red-600)" }}>{unmatched.length}</div>
        </div>
      </div>

      <div className="admin-card" style={{ marginBottom: 16 }}>
        <div className="admin-card-header">
          <h3>Manual credit</h3>
        </div>
        <div className="admin-inline-form" style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center", marginTop: 8 }}>
          <input className="ev-input" placeholder="Agent ID" value={agentId} onChange={(e) => setAgentId(e.target.value)} style={{ minWidth: 200 }} />
          <input className="ev-input" type="number" min="1" placeholder="Amount (TZS)" value={amount} onChange={(e) => setAmount(e.target.value)} style={{ width: 160 }} />
          <input className="ev-input" placeholder="Reference (optional)" value={reference} onChange={(e) => setReference(e.target.value)} style={{ minWidth: 200 }} />
          <button
            className="ev-btn ev-btn-sm ev-btn-primary"
            disabled={credit.isPending || !agentId.trim() || Number(amount) <= 0}
            onClick={() =>
              credit.mutate({
                agent_id: agentId.trim(),
                amount_minor: Math.round(Number(amount) * 100),
                reference,
              })
            }
          >
            <Banknote size={14} /> Credit wallet
          </button>
        </div>
        {feedback && <div className="ev-body-micro" style={{ marginTop: 8, color: "var(--ev-mint-600)" }}>{feedback}</div>}
        {actionError && (
          <div className="admin-note" style={{ marginTop: 8 }}>
            <AlertTriangle size={16} />
            <span>{actionError}</span>
          </div>
        )}
      </div>

      <div className="admin-card-header" style={{ marginBottom: 16 }}>
        <div className="admin-toggle-group">
          {(["all", "credited", "pending", "awaiting_payment", "failed", "expired"] as const).map((s) => (
            <button
              key={s}
              className={`admin-toggle ${statusFilter === s ? "admin-toggle-active" : ""}`}
              onClick={() => { setStatusFilter(s); setPage(1); }}
            >
              {s === "all" ? "All" : s.replace("_", " ")}
            </button>
          ))}
        </div>
      </div>

      {list.isLoading && <LoadingCard label="Loading funding orders…" />}
      {list.error && <ErrorCard error={list.error} onRetry={() => void list.refetch()} />}
      {list.data && rows.length === 0 && <EmptyState>No funding orders for this filter.</EmptyState>}

      {rows.length > 0 && (
        <>
          <div className="admin-card" style={{ padding: 0, overflow: "hidden" }}>
            <div className="admin-table-wrap">
              <table className="admin-table">
                <thead>
                  <tr>
                    <th className="admin-table-th">Reference</th>
                    <th className="admin-table-th">Agent</th>
                    <th className="admin-table-th">Amount</th>
                    <th className="admin-table-th">Method</th>
                    <th className="admin-table-th">Status</th>
                    <th className="admin-table-th">Created</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((f) => (
                    <tr key={f.id} className="admin-table-row-hover">
                      <td className="admin-table-td" data-label="Reference"><span className="ev-body-mono ev-body-semibold">{f.payment_reference}</span></td>
                      <td className="admin-table-td" data-label="Agent">
                        <div className="admin-cell-stack">
                          <span className="ev-body-semibold">{f.agent?.business_name ?? f.agent_id.slice(0, 10)}</span>
                          <span className="ev-body-micro muted">{f.agent?.phone}</span>
                        </div>
                      </td>
                      <td className="admin-table-td" data-label="Amount"><span className="ev-body-mono ev-body-semibold" style={{ color: "var(--ev-mint-600)" }}>{f.amount_display}</span></td>
                      <td className="admin-table-td" data-label="Method">{f.method}</td>
                      <td className="admin-table-td" data-label="Status">
                        <span className="admin-status" style={{ background: statusBg[f.status] ?? "var(--ev-slate-100)", color: statusColor[f.status] ?? "var(--ev-slate-600)" }}>
                          {f.status === "credited" ? <Check size={14} /> : f.status.startsWith("await") || f.status === "pending" ? <ArrowUp size={14} /> : <X size={14} />}
                          <span style={{ textTransform: "capitalize" }}>{f.status.replace("_", " ")}</span>
                        </span>
                      </td>
                      <td className="admin-table-td" data-label="Created"><span className="ev-body-micro muted">{shortDateTime(f.created_at)}</span></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {total > pageSize && (
            <div className="admin-pagination">
              <button className="ev-btn ev-btn-sm ev-btn-ghost" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>
                <ChevronLeft size={16} /> Previous
              </button>
              <span className="ev-body-micro">Page {page} · {total} funding orders</span>
              <button className="ev-btn ev-btn-sm ev-btn-ghost" disabled={page * pageSize >= total} onClick={() => setPage((p) => p + 1)}>
                Next <ChevronRight size={16} />
              </button>
            </div>
          )}
        </>
      )}

      <div className="admin-card" style={{ marginTop: 24, padding: 0, overflow: "hidden" }}>
        <div className="admin-card-header" style={{ padding: "16px 20px", marginBottom: 0 }}>
          <h3>Unmatched receipts</h3>
          <span className="ev-body-micro muted">Allocate provider receipts to an agent wallet</span>
        </div>
        {clearing.isLoading && <div style={{ padding: 20 }}><LoadingCard label="Loading clearing…" /></div>}
        {clearing.error && <div style={{ padding: 20 }}><ErrorCard error={clearing.error} /></div>}
        {clearing.data && unmatched.length === 0 && (
          <div style={{ padding: 20 }}><span className="ev-body-muted">No unmatched receipts.</span></div>
        )}
        {unmatched.length > 0 && (
          <div className="admin-table-wrap">
            <table className="admin-table">
              <thead>
                <tr>
                  <th className="admin-table-th">Provider ref</th>
                  <th className="admin-table-th">Provider</th>
                  <th className="admin-table-th">Amount</th>
                  <th className="admin-table-th">Received</th>
                  <th className="admin-table-th">Wallet to allocate</th>
                </tr>
              </thead>
              <tbody>
                {unmatched.map((row) => (
                  <tr key={row.id} className="admin-table-row-hover">
                    <td className="admin-table-td" data-label="Provider ref"><span className="ev-body-mono">{row.provider_ref}</span></td>
                    <td className="admin-table-td" data-label="Provider">{row.provider}</td>
                    <td className="admin-table-td" data-label="Amount"><span className="ev-body-mono ev-body-semibold">{tzs(row.amount_minor)}</span></td>
                    <td className="admin-table-td" data-label="Received"><span className="ev-body-micro muted">{shortDateTime(row.created_at)}</span></td>
                    <td className="admin-table-td" data-label="Wallet to allocate">
                      <div style={{ display: "flex", gap: 6 }}>
                        <input
                          className="ev-input"
                          placeholder="Wallet ID"
                          value={walletInputs[row.id] ?? ""}
                          onChange={(e) => setWalletInputs((prev) => ({ ...prev, [row.id]: e.target.value }))}
                          style={{ width: 220 }}
                        />
                        <button
                          className="ev-btn ev-btn-sm ev-btn-outline"
                          disabled={allocate.isPending || !(walletInputs[row.id] ?? "").trim()}
                          onClick={() => allocate.mutate({ ref: row.id, wallet_id: (walletInputs[row.id] ?? "").trim() })}
                        >
                          <ArrowDown size={14} /> Allocate
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </>
  );
}
